import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllRichTextsForStep,
  getAllAttachmentsForStep,
  getAllFormsForStep,
  getAllVideosForStep,
  getAllCanvasesForStep,
} from '../../../../../redux/stepSlice'
import RenderHtml from '../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml'
import ChildrenRenderer from '../../../../Documents/components/Nodes/ChildrenRenderer'
import Loader from '../../../../../common/Loader'

const PreviewSection = ({version_content}) => {
  const { section_name, short_description } = version_content
  const dispatch = useDispatch()
  const [steps, setSteps] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  
  useEffect(() => {
    const fetchStepsData = async () => {
      const sectionId = version_content.section_tree_node_id
      const partId = version_content.part_id_ref
      const revisionId = version_content.revision_id_ref
      
      if (sectionId && partId && revisionId) {
        setIsLoading(true)
        try {
          // Fetch all steps for this section
          const stepsResp = await dispatch(
            getAllStepsForSection({ partId, revisionId, sectionId })
          )
          
          const stepsData = stepsResp.payload?.data || []
          
          // For each step, fetch its children and substeps
          const stepsWithChildren = await Promise.all(
            stepsData.map(async (step) => {
              const stepId = step.step_tree_node_id
              
              if (stepId) {
                const [richTextsResp, attachmentsResp, formsResp, videosResp, canvasesResp, substepsResp] = await Promise.all([
                  dispatch(getAllRichTextsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
                  dispatch(getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
                  dispatch(getAllFormsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
                  dispatch(getAllVideosForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
                  dispatch(getAllCanvasesForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
                  dispatch(getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })),
                ])
                
                const stepChildren = [
                  ...(richTextsResp.payload?.data || []).map(item => ({ ...item, type: 'richText' })),
                  ...(attachmentsResp.payload?.data || []).map(item => ({ ...item, type: 'attachment' })),
                  ...(formsResp.payload?.data || []).map(item => ({ ...item, type: 'form' })),
                  ...(videosResp.payload?.data || []).map(item => ({ ...item, type: 'video' })),
                  ...(canvasesResp.payload?.data || []).map(item => ({ ...item, type: 'canvas' })),
                ]
                
                const substeps = substepsResp.payload?.data || []
                
                // For each substep, fetch its children
                const substepsWithChildren = await Promise.all(
                  substeps.map(async (substep) => {
                    const substepId = substep.sub_step_tree_node_id
                    if (substepId) {
                      const [subRichTexts, subAttachments, subForms, subVideos, subCanvases] = await Promise.all([
                        dispatch(getAllRichTextsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                        dispatch(getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                        dispatch(getAllFormsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                        dispatch(getAllVideosForStep({ partId, revisionId, sectionId, stepId, substepId })),
                        dispatch(getAllCanvasesForStep({ partId, revisionId, sectionId, stepId, substepId })),
                      ])
                      
                      const substepChildren = [
                        ...(subRichTexts.payload?.data || []).map(item => ({ ...item, type: 'richText' })),
                        ...(subAttachments.payload?.data || []).map(item => ({ ...item, type: 'attachment' })),
                        ...(subForms.payload?.data || []).map(item => ({ ...item, type: 'form' })),
                        ...(subVideos.payload?.data || []).map(item => ({ ...item, type: 'video' })),
                        ...(subCanvases.payload?.data || []).map(item => ({ ...item, type: 'canvas' })),
                      ]
                      
                      return {
                        ...substep,
                        type: 'substep',
                        children: substepChildren,
                      }
                    }
                    return { ...substep, type: 'substep', children: [] }
                  })
                )
                
                return {
                  ...step,
                  children: stepChildren,
                  substeps: substepsWithChildren,
                }
              }
              return step
            })
          )
          
          setSteps(stepsWithChildren)
        } catch (error) {
          console.error('Error fetching steps data:', error)
        } finally {
          setIsLoading(false)
        }
      }
    }
    
    fetchStepsData()
  }, [dispatch, version_content])
  
  if (isLoading) {
    return <Loader text="Loading section data..." />
  }
  
  // Use the section property directly from version_content which has the correct format "Section X"
  const sectionLabel = version_content.section || `Section ${version_content.procedure_tree?.sibling_order || version_content.sibling_order || '1'}`
  
  return (
    <div>
      <div style={{ padding: '20px' }}>
        {/* Section Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            paddingBottom: '20px',
            borderBottom: '3px double #e0e0e0',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" defaultChecked={true} readOnly disabled />
            <h2 style={{ margin: 0, fontSize: '23px' }}>{sectionLabel}</h2>
          </div>
        </div>

        {/* Section Name */}
        {section_name && (
          <p style={{ marginLeft: '5px' }}>
            {section_name}
          </p>
        )}

        {/* Section Description */}
        {short_description && (
          <p style={{ marginLeft: '5px', whiteSpace: 'pre-wrap' }}>
            {short_description}
          </p>
        )}

        {/* Steps */}
        {steps.map((step, stepIndex) => {
          // Use the step's sibling_order for proper numbering
          const stepNumber = step.procedure_tree?.sibling_order || step.sibling_order || (stepIndex + 1)
          
          return (
            <div key={step.step_tree_node_id} style={{ marginTop: '20px' }}>
              {/* Step Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderBottom: '1px solid #e0e0e0',
                  paddingBottom: '10px',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={true} disabled={true} readOnly />
                  <h3 style={{ margin: 0, fontSize: '23px' }}>Step {stepNumber}</h3>
                </div>
                <div style={{ color: '#888', fontSize: '14px', fontWeight: 'normal' }}>
                  {sectionLabel} &gt; Step {stepNumber}
                </div>
              </div>

              {/* Step Description */}
              {step.short_description && (
                <p style={{ marginLeft: '5px', whiteSpace: 'pre-wrap' }}>
                  {step.short_description}
                </p>
              )}

              {/* Step Details */}
              {step.step_details && step.step_details.length > 0 && (
                <div style={{ marginLeft: '5px' }}>
                  {structuredClone(step.step_details)
                    .sort((a, b) => {
                      const hasOrdersA = a.hasOwnProperty('orders')
                      const hasOrdersB = b.hasOwnProperty('orders')
                      
                      if (hasOrdersA && hasOrdersB) {
                        const orderA = parseInt(a.orders ?? 0, 10)
                        const orderB = parseInt(b.orders ?? 0, 10)
                        if (orderA !== orderB) return orderA - orderB
                        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
                      }
                      
                      return new Date(a.created_at || 0) - new Date(b.created_at || 0)
                    })
                    .map((myStep, index) => {
                      const partNumber = myStep.part_info?.part_number || ''
                      const description = myStep.part_info?.description || ''
                      const qtyText = myStep.qty && myStep.qty !== 0 ? `Qty ${myStep.qty}` : ''
                      const partText = partNumber ? ` (${partNumber})` : ''
                      return (
                        <span key={`step-detail-${index}`}>
                          {myStep.action && <>{myStep.action} </>}
                          {partText && <>{partText} </>}
                          {description && (
                            <>
                              <RenderHtml content={description} />{' '}
                            </>
                          )}
                          {qtyText && <>{qtyText}</>}&nbsp;
                        </span>
                      )
                    })}
                </div>
              )}

              {/* Parts Required Table (BOM Table) */}
              {step.parts_required && step.parts_required.length > 0 && (
                <div style={{ marginLeft: '5px', marginTop: '10px' }}>
                  <h4>{step.bom_table_title_info?.category_name || ''}</h4>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>{step.part_number_column_title_ref_info?.category_name}</th>
                        <th>Description</th>
                        <th>Qty Per</th>
                        <th>Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...step.parts_required]
                        .sort((a, b) => {
                          const orderA = parseInt(a.part_info?.type_info?.orders || 999999, 10)
                          const orderB = parseInt(b.part_info?.type_info?.orders || 999999, 10)
                          if (orderA !== orderB) return orderA - orderB

                          const partNumberA = a.part_info?.part_number || ''
                          const partNumberB = b.part_info?.part_number || ''

                          const splitPart = (str) => {
                            const match = str.match(/^(\d+)?([A-Za-z].*)?$/)
                            if (!match) return { num: NaN, rest: str }
                            return {
                              num: match[1] ? parseInt(match[1], 10) : NaN,
                              rest: match[2] || str,
                            }
                          }
                          const aSplit = splitPart(partNumberA)
                          const bSplit = splitPart(partNumberB)

                          if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1
                          if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1
                          if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                            if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num
                            return aSplit.rest.localeCompare(bSplit.rest)
                          }
                          return partNumberA.localeCompare(partNumberB)
                        })
                        .map((row, idx) => (
                          <tr key={`part-required-${idx}`}>
                            <td>{row.part_info?.type_info?.name || ''}</td>
                            <td>{row.part_info?.part_number || ''}</td>
                            <td>
                              <RenderHtml content={row.part_info?.description} />
                            </td>
                            <td>{row.qty === 0 ? '' : row.qty}</td>
                            <td>{row.part_info?.unit_info?.unit_name || ''}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Step-level Children */}
              {step.children && step.children.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <ChildrenRenderer
                    children={step.children}
                    parentContext={{
                      sectionIndex: 0,
                      stepIndex: stepIndex,
                      globalStepNumber: stepNumber,
                      sectionHeading: section_name || 'Section',
                      sectionId: version_content.section_tree_node_id,
                      stepId: step.step_tree_node_id,
                      partNumber: version_content.partNumber,
                      revision: version_content.revision,
                      partId: version_content.part_id_ref,
                      revisionId: version_content.revision_id_ref,
                    }}
                    onNavigate={() => {}}
                    checkboxState={{
                      currentNodeId: step.step_tree_node_id,
                      currentNodeType: 'step',
                      allChildrenChecked: true,
                      allChildrenDisabled: true,
                    }}
                    disabled={true}
                    allNodesData={[]}
                  />
                </div>
              )}

              {/* Substeps */}
              {step.substeps && step.substeps.length > 0 && (
                <>
                  {step.substeps.map((substep, substepIndex) => {
                    const substepLetter = String.fromCharCode(65 + substepIndex)
                    // Use the step's actual number for substep labeling
                    const actualStepNumber = step.procedure_tree?.sibling_order || step.sibling_order || (stepIndex + 1)
                    const globalSubstepLabel = `Substep ${actualStepNumber}${substepLetter}`

                    return (
                      <div key={substep.sub_step_tree_node_id} style={{ marginTop: '20px' }}>
                        <div>
                          <label style={{ display: 'flex', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" disabled={true} checked={true} readOnly />
                            <h2 style={{ margin: 0 }}>{globalSubstepLabel}</h2>
                          </label>

                          <div style={{ marginLeft: '5px', whiteSpace: 'pre-wrap' }}>
                            {substep.short_description}
                          </div>
                        </div>

                        {/* Substep Children */}
                        {substep.children && substep.children.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <ChildrenRenderer
                              children={substep.children}
                              parentContext={{
                                sectionIndex: 0,
                                stepIndex: stepIndex,
                                substepIndex: substepIndex,
                                globalStepNumber: stepNumber,
                                sectionHeading: section_name || 'Section',
                                sectionId: version_content.section_tree_node_id,
                                stepId: step.step_tree_node_id,
                                substepId: substep.sub_step_tree_node_id,
                                partNumber: version_content.partNumber,
                                revision: version_content.revision,
                                partId: version_content.part_id_ref,
                                revisionId: version_content.revision_id_ref,
                              }}
                              onNavigate={() => {}}
                              checkboxState={{
                                currentNodeId: substep.sub_step_tree_node_id,
                                currentNodeType: 'substep',
                                allChildrenChecked: true,
                                allChildrenDisabled: true,
                              }}
                              disabled={true}
                              allNodesData={[]}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PreviewSection